import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../api';

// Simple Tailwind Modal for feedback
function FeedbackModal({ open, message, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-xs w-full flex flex-col items-center">
        <div className="text-slate-700 text-center text-sm font-semibold mb-4">{message}</div>
        <button
          className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded font-bold text-xs hover:bg-indigo-700 transition"
          onClick={onClose}
        >OK</button>
      </div>
    </div>
  );
}

// ==================== SIMPLIFIED MAPPING POPOVER ====================
const MappingRow = ({ type, mappings, totalCount, onUpdateMapping, onAddMapping, onRemoveMapping, marks, disabled }) => {
  const totalWeightage = mappings.reduce((sum, m) => sum + (m.weightage || 0), 0);
  const isValid = Math.abs(totalWeightage - 100) < 0.01;
  const typeUpper = type.toUpperCase();

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 min-w-[140px] flex-1">
      <div className="flex justify-between items-center mb-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold tracking-wider uppercase text-slate-500">{typeUpper}</span>
          {mappings.length > 0 && (
            <span className={`text-[9px] font-black px-1.5 py-0.2 rounded ${isValid ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
              {totalWeightage}%
            </span>
          )}
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={onAddMapping}
            className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
          >
            + Link
          </button>
        )}
      </div>

      {mappings.length === 0 ? (
        <div className="text-[10px] text-slate-400 italic text-center py-1 bg-white border border-dashed rounded">None</div>
      ) : (
        <div className="space-y-1">
          {mappings.map((mapping, idx) => (
            <div key={idx} className="flex items-center gap-1 bg-white p-1 border rounded shadow-sm flex-wrap sm:flex-nowrap">
              <select
                value={mapping[type === 'co' ? 'coNumber' : 'poNumber']}
                onChange={(e) => onUpdateMapping(idx, type === 'co' ? 'coNumber' : 'poNumber', parseInt(e.target.value, 10))}
                className="text-[11px] font-bold bg-slate-50 border rounded p-0.5 focus:ring-1 focus:ring-indigo-500 outline-none"
                disabled={disabled}
              >
                {[...Array(totalCount)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>{typeUpper}{i + 1}</option>
                ))}
              </select>

              <div className="flex items-center gap-0.5 max-w-[55px]">
                <input
                  type="number"
                  value={mapping.weightage || 0}
                  onChange={(e) => onUpdateMapping(idx, 'weightage', Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                  className="w-full text-center text-[11px] font-bold border rounded p-0.5 focus:ring-1 focus:ring-indigo-500"
                  min="0"
                  max="100"
                  disabled={disabled}
                />
              </div>

              <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-50 px-1 rounded ml-auto shrink-0 border border-slate-100">
                {((marks * (mapping.weightage || 0)) / 100).toFixed(1)}m
              </span>

              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemoveMapping(idx)}
                  className="text-slate-400 hover:text-rose-600 font-bold px-0.5 text-xs transition-colors"
                >
                  &times;
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== SUB-QUESTION ROW ELEMENT ====================
const SubQuestionRow = React.memo(({ subQuestion, subIndex, onUpdate, onRemove, coPoCounts, enforceCOPOMapping }) => {
  return (
    <div className="flex flex-col lg:flex-row gap-3 bg-slate-50/70 border border-slate-200/80 p-3 rounded-lg items-stretch lg:items-center pl-5 relative shadow-sm">
      {/* Structural Indentation Bracket Indicator */}
      <div className="absolute left-2.5 top-0 bottom-0 w-0.5 bg-slate-300"></div>
      
      {/* Sub-Question Custom Sequence Marker input */}
      <div className="w-full lg:w-28 shrink-0 flex items-center gap-2">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block lg:hidden">Sub-No:</span>
        <input
          type="text"
          value={subQuestion.subQuestionNumber || ''}
          placeholder={`e.g., ${['a', 'b', 'c'][subIndex] || 'z'}`}
          onChange={(e) => onUpdate('subQuestionNumber', e.target.value)}
          className="w-full text-xs font-black bg-white border border-slate-300 rounded-md p-1.5 text-center shadow-inner uppercase focus:ring-2 focus:ring-indigo-500/20"
        />
      </div>

      <div className="w-full lg:w-28 shrink-0">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block lg:hidden mb-0.5">Marks Weight:</span>
        <input
          type="number"
          value={subQuestion.marks || ''}
          placeholder="Marks"
          onChange={(e) => onUpdate('marks', Math.max(0, parseFloat(e.target.value) || 0))}
          className="w-full text-xs font-black border border-slate-300 rounded-md p-1.5 focus:ring-2 focus:ring-indigo-500/20"
          min="0"
          step="0.5"
        />
      </div>

      {/* Taxonomy Level - Only shown when CO/PO mapping is enforced */}
      {enforceCOPOMapping && (
        <div className="w-full lg:w-40 shrink-0">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block lg:hidden mb-0.5">Taxonomy Level:</span>
          <select
            value={subQuestion.bloomLevel || 'understand'}
            onChange={(e) => onUpdate('bloomLevel', e.target.value)}
            className="w-full text-xs font-semibold border border-slate-300 rounded-md p-1.5 bg-white shadow-sm"
          >
            <option value="remember">🧠 Remember</option>
            <option value="understand">📖 Understand</option>
            <option value="apply">⚙️ Apply</option>
            <option value="analyze">🔍 Analyze</option>
            <option value="evaluate">⚖️ Evaluate</option>
            <option value="create">🎨 Create</option>
          </select>
        </div>
      )}

      {/* CO/PO Mapping Section - Only shown when enforced */}
      {enforceCOPOMapping && (
        <div className="flex-1 flex flex-col sm:flex-row gap-2 min-w-0">
          <MappingRow
            type="co"
            mappings={subQuestion.coMappings || []}
            totalCount={coPoCounts.totalCOs}
            onUpdateMapping={(mIdx, f, v) => {
              const arr = [...(subQuestion.coMappings || [])];
              arr[mIdx][f] = v;
              onUpdate('coMappings', arr);
            }}
            onAddMapping={() => onUpdate('coMappings', [...(subQuestion.coMappings || []), { coNumber: 1, weightage: 100 }])}
            onRemoveMapping={(mIdx) => onUpdate('coMappings', subQuestion.coMappings.filter((_, i) => i !== mIdx))}
            marks={subQuestion.marks}
          />
          <MappingRow
            type="po"
            mappings={subQuestion.poMappings || []}
            totalCount={coPoCounts.totalPOs}
            onUpdateMapping={(mIdx, f, v) => {
              const arr = [...(subQuestion.poMappings || [])];
              arr[mIdx][f] = v;
              onUpdate('poMappings', arr);
            }}
            onAddMapping={() => onUpdate('poMappings', [...(subQuestion.poMappings || []), { poNumber: 1, weightage: 100 }])}
            onRemoveMapping={(mIdx) => onUpdate('poMappings', subQuestion.poMappings.filter((_, i) => i !== mIdx))}
            marks={subQuestion.marks}
          />
        </div>
      )}

      <div className="w-full lg:w-16 shrink-0 text-right self-end lg:self-center mt-2 lg:mt-0">
        <button
          type="button"
          onClick={() => onRemove(subIndex)}
          className="w-full lg:w-auto text-xs font-bold text-rose-500 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200/60 px-2 py-1 rounded transition-colors"
        >
          Drop
        </button>
      </div>
    </div>
  );
});

// ==================== PRIMARY QUESTION LEDGER ROW ====================
const QuestionItem = React.memo(({ 
  question, index, onUpdate, onRemove, onAddSubQuestion, onUpdateSubQuestion, onRemoveSubQuestion, coPoCounts, enforceCOPOMapping 
}) => {
  const hasSubQuestions = question.subQuestions && question.subQuestions.length > 0;
  const totalSubMarks = question.subQuestions?.reduce((sum, sq) => sum + (sq.marks || 0), 0) || 0;
  const marksMismatch = hasSubQuestions && Math.abs(totalSubMarks - question.marks) > 0.01;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden p-4 space-y-4 transition-all hover:border-slate-300">
      
      {/* Dynamic Grid Alignment Node */}
      <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
        
        {/* Manual Configuration Question Tracking Input identifier field */}
        <div className="w-full lg:w-28 shrink-0">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Question ID:</label>
          <input
            type="text"
            value={question.questionNumber || ''}
            placeholder={`Q${index + 1}`}
            onChange={(e) => onUpdate('questionNumber', e.target.value)}
            className="w-full text-xs font-black bg-slate-50 border border-slate-300 rounded-md p-2 text-center shadow-inner focus:ring-2 focus:ring-indigo-500/20 uppercase"
          />
        </div>

        <div className="w-full lg:w-28 shrink-0">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Total Marks:</label>
          <div className="relative">
            <input
              type="number"
              value={question.marks || ''}
              placeholder="Marks"
              disabled={hasSubQuestions}
              onChange={(e) => onUpdate('marks', Math.max(0, parseFloat(e.target.value) || 0))}
              className="w-full text-xs font-black border border-slate-300 rounded-md p-2 bg-white focus:ring-2 focus:ring-indigo-500/20 disabled:bg-slate-100 disabled:text-slate-500"
              min="0"
              step="0.5"
            />
            {hasSubQuestions && <span className="absolute right-2 top-2.5 text-[9px] font-bold text-slate-400 bg-white px-0.5">Sum</span>}
          </div>
          {marksMismatch && (
            <span className="text-[9px] font-bold text-amber-600 block mt-0.5 bg-amber-50 rounded px-1 text-center border border-amber-100">
              Mismatch ({totalSubMarks}m)
            </span>
          )}
        </div>

        {/* Taxonomy Target - Only shown when CO/PO mapping is enforced */}
        {enforceCOPOMapping && !hasSubQuestions && (
          <div className="w-full lg:w-40 shrink-0">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Taxonomy Target:</label>
            <select
              value={question.bloomLevel || 'understand'}
              onChange={(e) => onUpdate('bloomLevel', e.target.value)}
              className="w-full text-xs font-semibold border border-slate-300 rounded-md p-2 bg-white focus:ring-2 focus:ring-indigo-500/20 shadow-sm"
            >
              <option value="remember">🧠 Remember</option>
              <option value="understand">📖 Understand</option>
              <option value="apply">⚙️ Apply</option>
              <option value="analyze">🔍 Analyze</option>
              <option value="evaluate">⚖️ Evaluate</option>
              <option value="create">🎨 Create</option>
            </select>
          </div>
        )}

        {/* When CO/PO not enforced and no sub-questions, add empty divs to maintain layout */}
        {!enforceCOPOMapping && !hasSubQuestions && (
          <>
            <div className="w-full lg:w-40 shrink-0"></div>
            <div className="flex-1"></div>
          </>
        )}

        {/* When has sub-questions and CO/PO enforced, show the multi-part message */}
        {hasSubQuestions && enforceCOPOMapping && (
          <div className="flex-1 w-full text-[11px] font-semibold text-indigo-600 bg-indigo-50/60 rounded-lg p-2.5 border border-indigo-100 flex items-center justify-center text-center">
            ✓ Multi-part distribution active across {question.subQuestions.length} child vectors.
          </div>
        )}

        {/* When has sub-questions and CO/PO not enforced, just show a simple message */}
        {hasSubQuestions && !enforceCOPOMapping && (
          <div className="flex-1 w-full text-[11px] font-semibold text-slate-500 bg-slate-50 rounded-lg p-2.5 border border-slate-200 flex items-center justify-center text-center">
            Multi-part distribution active across {question.subQuestions.length} child vectors.
          </div>
        )}

        {/* Outcome Mappings Desk - Only shown when enforceCOPOMapping is true and no sub-questions */}
        {!hasSubQuestions && enforceCOPOMapping && (
          <div className="flex-1 flex flex-col sm:flex-row gap-2 min-w-0">
            <MappingRow
              type="co"
              mappings={question.coMappings || []}
              totalCount={coPoCounts.totalCOs}
              onUpdateMapping={(mIdx, f, v) => {
                const arr = [...(question.coMappings || [])];
                arr[mIdx][f] = v;
                onUpdate('coMappings', arr);
              }}
              onAddMapping={() => onUpdate('coMappings', [...(question.coMappings || []), { coNumber: 1, weightage: 100 }])}
              onRemoveMapping={(mIdx) => onUpdate('coMappings', question.coMappings.filter((_, i) => i !== mIdx))}
              marks={question.marks}
            />
            <MappingRow
              type="po"
              mappings={question.poMappings || []}
              totalCount={coPoCounts.totalPOs}
              onUpdateMapping={(mIdx, f, v) => {
                const arr = [...(question.poMappings || [])];
                arr[mIdx][f] = v;
                onUpdate('poMappings', arr);
              }}
              onAddMapping={() => onUpdate('poMappings', [...(question.poMappings || []), { poNumber: 1, weightage: 100 }])}
              onRemoveMapping={(mIdx) => onUpdate('poMappings', question.poMappings.filter((_, i) => i !== mIdx))}
              marks={question.marks}
            />
          </div>
        )}

        {/* Action Panel Controllers */}
        <div className="w-full lg:w-20 shrink-0 flex flex-row lg:flex-col justify-end lg:justify-center gap-1 pt-2 lg:pt-0 border-t lg:border-t-0 border-slate-100">
          <button
            type="button"
            onClick={onRemove}
            className="flex-1 lg:flex-none text-xs font-bold text-rose-500 hover:bg-rose-50 border border-transparent hover:border-rose-200 px-2.5 py-1.5 rounded-md transition-colors text-center"
          >
            Remove
          </button>
          {!hasSubQuestions && (
            <button
              type="button"
              onClick={onAddSubQuestion}
              className="flex-1 lg:flex-none text-[11px] font-bold text-indigo-600 bg-indigo-50/50 hover:bg-indigo-50 border border-indigo-200/60 px-2.5 py-1.5 rounded-md transition-all text-center"
            >
              Split (a,b)
            </button>
          )}
        </div>
      </div>

      {/* Sub-parts Rendering Matrix Stack */}
      {hasSubQuestions && (
        <div className="mt-3 space-y-2 pt-3 border-t border-slate-100">
          <div className="flex justify-between items-center pl-5">
            <span className="text-[10px] font-black text-slate-400 tracking-wider uppercase">Bifurcated Allocation Stack</span>
            <button
              type="button"
              onClick={onAddSubQuestion}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              + Append Part
            </button>
          </div>
          <div className="space-y-2">
            {question.subQuestions.map((subQ, subIdx) => (
              <SubQuestionRow
                key={subIdx}
                subQuestion={subQ}
                subIndex={subIdx}
                onUpdate={(f, v) => onUpdateSubQuestion(subIdx, f, v)}
                onRemove={onRemoveSubQuestion}
                coPoCounts={coPoCounts}
                enforceCOPOMapping={enforceCOPOMapping}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

// ==================== SECTION WORKSPACE MATRIX PANEL ====================
const SectionPanel = React.memo(({ section, onUpdateSection, coPoCounts, enforceCOPOMapping }) => {

  const questions = useMemo(() => section.questions || [], [section.questions]);
  const sectionTotal = questions.reduce((sum, q) => sum + (q.marks || 0), 0);
  const isOver = sectionTotal > section.sectionMaxMarks;

  const updateQuestion = useCallback((qIdx, field, value) => {
    const newQuestions = [...questions];
    newQuestions[qIdx][field] = value;
    onUpdateSection({ ...section, questions: newQuestions });
  }, [questions, section, onUpdateSection]);

  const addQuestion = useCallback(() => {
    const nextNum = questions.length + 1;
    const newQuestions = [...questions, {
      questionNumber: `Q${nextNum}`,
      marks: 0,
      coMappings: enforceCOPOMapping ? [{ coNumber: 1, weightage: 100 }] : [],
      poMappings: enforceCOPOMapping ? [{ poNumber: 1, weightage: 100 }] : [],
      bloomLevel: enforceCOPOMapping ? 'understand' : '',
      subQuestions: []
    }];
    onUpdateSection({ ...section, questions: newQuestions });
  }, [questions, section, onUpdateSection, enforceCOPOMapping]);

  const removeQuestion = useCallback((qIdx) => {
    const newQuestions = questions.filter((_, i) => i !== qIdx);
    onUpdateSection({ ...section, questions: newQuestions });
  }, [questions, section, onUpdateSection]);

  const addSubQuestion = useCallback((qIdx) => {
    const newQuestions = [...questions];
    const q = { ...newQuestions[qIdx] };
    const subLetters = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    const newSubNumber = subLetters[q.subQuestions?.length || 0] || 'z';

    const baseSubQuestions = q.subQuestions ? [...q.subQuestions] : [];
    if (baseSubQuestions.length === 0) {
      q.coMappings = [];
      q.poMappings = [];
      q.bloomLevel = '';
    }

    q.subQuestions = [...baseSubQuestions, {
      subQuestionNumber: newSubNumber,
      marks: 0,
      coMappings: enforceCOPOMapping ? [{ coNumber: 1, weightage: 100 }] : [],
      poMappings: enforceCOPOMapping ? [{ poNumber: 1, weightage: 100 }] : [],
      bloomLevel: enforceCOPOMapping ? 'understand' : ''
    }];

    newQuestions[qIdx] = q;
    onUpdateSection({ ...section, questions: newQuestions });
  }, [questions, section, onUpdateSection, enforceCOPOMapping]);

  const updateSubQuestion = useCallback((qIdx, subIdx, field, value) => {
    const newQuestions = [...questions];
    const q = { ...newQuestions[qIdx] };
    const subs = [...(q.subQuestions || [])];
    subs[subIdx] = { ...subs[subIdx], [field]: value };
    q.subQuestions = subs;
    q.marks = subs.reduce((sum, sq) => sum + (sq.marks || 0), 0);
    newQuestions[qIdx] = q;
    onUpdateSection({ ...section, questions: newQuestions });
  }, [questions, section, onUpdateSection]);

  const removeSubQuestion = useCallback((qIdx, subIdx) => {
    const newQuestions = [...questions];
    const q = { ...newQuestions[qIdx] };
    const subs = (q.subQuestions || []).filter((_, i) => i !== subIdx);

    if (subs.length === 0) {
      q.coMappings = enforceCOPOMapping ? [{ coNumber: 1, weightage: 100 }] : [];
      q.poMappings = enforceCOPOMapping ? [{ poNumber: 1, weightage: 100 }] : [];
      q.bloomLevel = enforceCOPOMapping ? 'understand' : '';
      q.marks = 0;
      q.subQuestions = [];
    } else {
      q.subQuestions = subs;
      q.marks = subs.reduce((sum, sq) => sum + (sq.marks || 0), 0);
    }

    newQuestions[qIdx] = q;
    onUpdateSection({ ...section, questions: newQuestions });
  }, [questions, section, onUpdateSection, enforceCOPOMapping]);

  return (
    <div className="space-y-4">
      <div className="bg-slate-100 border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h3 className="text-xs font-black uppercase tracking-wider text-slate-700">Section {section.sectionName} Workspace</h3>
          <p className="text-[11px] text-slate-400 font-bold mt-0.5">Maximum Structural Cap Limit: {section.sectionMaxMarks} marks</p>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-200">
          <span className={`text-sm font-mono font-black ${isOver ? 'text-rose-600' : 'text-emerald-600'}`}>
            Summed: {sectionTotal} / {section.sectionMaxMarks}m
          </span>
          <button
            type="button"
            onClick={addQuestion}
            className="px-4 py-2 text-xs font-black bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-sm transition-all"
          >
            + Add Question
          </button>
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-slate-300">
          <p className="text-xs text-slate-400 font-medium">No question frameworks are mapped inside this section cluster.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map((question, qIdx) => (
            <QuestionItem
              key={question._id || `q-idx-${qIdx}`}
              question={question}
              index={qIdx}
              onUpdate={(f, v) => updateQuestion(qIdx, f, v)}
              onRemove={() => removeQuestion(qIdx)}
              onAddSubQuestion={() => addSubQuestion(qIdx)}
              onUpdateSubQuestion={(sIdx, f, v) => updateSubQuestion(qIdx, sIdx, f, v)}
              onRemoveSubQuestion={(sIdx) => removeSubQuestion(qIdx, sIdx)}
              coPoCounts={coPoCounts}
              enforceCOPOMapping={enforceCOPOMapping}
            />
          ))}
        </div>
      )}
    </div>
  );
});

// ==================== MASTER SYSTEM WRAPPER MODAL ====================
export default function QuestionEditorModal({ 
  open, onClose, assignment, template, context, onSaveAndFinalize, onUpdateAssignment 
}) {
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [coPoCounts, setCoPoCounts] = useState({ totalCOs: 6, totalPOs: 12 });
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [isPreviewStage, setIsPreviewStage] = useState(false);
  const [acceptPreconditions, setAcceptPreconditions] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: '' });
  
  // Check if this is an End Exam assignment
  const isEndExam = context?.isEndExam === true || assignment?.isEndExam === true;
  
  // Check if CO/PO mapping is enforced by the template
  const enforceCOPOMapping = template?.enforceCOPOMapping || false;

  useEffect(() => {
    if (!open) return;
    const fetchCOPOCounts = async () => {
      try {
        const coPoEndpoint = isEndExam 
          ? '/api/faculty/end-exam-paper-templates/co-po-counts'
          : '/api/faculty/co-po-counts';
        
        const response = await api.get(coPoEndpoint, {
          params: { tenantId: context.tenantId, programTypeId: context.programTypeId, programId: context.programId }
        });
        if (response.data.success) setCoPoCounts(response.data.data);
      } catch (err) {
        console.error('Failed fetching threshold metric rules:', err);
      }
    };
    fetchCOPOCounts();
  }, [open, context, isEndExam]);

  useEffect(() => {
    if (!open) return;
    setIsPreviewStage(false);
    setAcceptPreconditions(false);

    const cleanQuestion = (q, idx) => {
      const hasSubQs = q.subQuestions && q.subQuestions.length > 0;
      const explicitNum = q.questionNumber || `Q${idx + 1}`;
      
      if (hasSubQs) {
        return {
          _id: q._id?.$oid || q._id,
          questionNumber: explicitNum,
          marks: q.marks,
          coMappings: [],
          poMappings: [],
          bloomLevel: '',
          subQuestions: (q.subQuestions || []).map((sq, sIdx) => ({
            subQuestionNumber: sq.subQuestionNumber || ['a', 'b', 'c'][sIdx] || 'z', 
            marks: sq.marks,
            coMappings: enforceCOPOMapping ? (sq.coMappings || [{ coNumber: 1, weightage: 100 }]) : [],
            poMappings: enforceCOPOMapping ? (sq.poMappings || [{ poNumber: 1, weightage: 100 }]) : [],
            bloomLevel: enforceCOPOMapping ? (sq.bloomLevel || 'understand') : ''
          }))
        };
      }
      return {
        _id: q._id?.$oid || q._id,
        questionNumber: explicitNum, 
        marks: q.marks,
        coMappings: enforceCOPOMapping && q.coMappings?.length > 0 ? q.coMappings : (enforceCOPOMapping ? [{ coNumber: 1, weightage: 100 }] : []),
        poMappings: enforceCOPOMapping && q.poMappings?.length > 0 ? q.poMappings : (enforceCOPOMapping ? [{ poNumber: 1, weightage: 100 }] : []),
        bloomLevel: enforceCOPOMapping ? (q.bloomLevel || 'understand') : '', 
        subQuestions: []
      };
    };

    if (assignment?.sections?.length > 0) {
      setSections(assignment.sections.map(s => ({
        sectionId: s.sectionId?.$oid || s.sectionId, 
        sectionName: s.sectionName, 
        sectionMaxMarks: s.sectionMaxMarks,
        questions: s.questions.map((q, i) => cleanQuestion(q, i)), 
        totalMarks: s.totalMarks
      })));
    } else if (template?.sections) {
      setSections(template.sections.map(s => ({
        sectionId: s._id, 
        sectionName: s.name, 
        sectionMaxMarks: s.maxMarks, 
        questions: [], 
        totalMarks: 0
      })));
    }
  }, [open, assignment, template, enforceCOPOMapping]);

  const totalMarks = useMemo(() => {
    return sections.reduce((sum, s) => sum + s.questions.reduce((acc, q) => acc + (Number(q.marks) || 0), 0), 0);
  }, [sections]);

  const targetTotalMarks = useMemo(() => {
    const templateMaxMarks = Number(template?.maxMarks);
    if (Number.isFinite(templateMaxMarks) && templateMaxMarks > 0) {
      return templateMaxMarks;
    }

    const sectionTotal = sections.reduce((sum, section) => sum + (Number(section.sectionMaxMarks) || 0), 0);
    return sectionTotal > 0 ? sectionTotal : 0;
  }, [template?.maxMarks, sections]);

  const validateSectionTotals = useCallback(() => {
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const total = section.questions.reduce((sum, q) => sum + (Number(q.marks) || 0), 0);
      if (total > section.sectionMaxMarks) {
        setError(`${section.sectionName} exceeds threshold constraints (${total}/${section.sectionMaxMarks}m configured).`);
        return false;
      }
    }
    setError(null);
    return true;
  }, [sections]);

  const validateFinalTotal = useCallback(() => {
    if (!validateSectionTotals()) return false;

    if (targetTotalMarks <= 0) {
      setError('Total marks target is not configured.');
      return false;
    }

    if (totalMarks !== targetTotalMarks) {
      setError(`Total configured marks must equal ${targetTotalMarks}. Current total is ${totalMarks}.`);
      return false;
    }

    setError(null);
    return true;
  }, [targetTotalMarks, totalMarks, validateSectionTotals]);

  useEffect(() => {
    if (open && sections.length > 0) validateSectionTotals();
  }, [sections, validateSectionTotals, open]);

  const buildPayload = () => sections.map(section => ({
    sectionId: section.sectionId, 
    sectionName: section.sectionName, 
    sectionMaxMarks: section.sectionMaxMarks,
    questions: section.questions.map((q, idx) => {
      const hasSubQs = q.subQuestions && q.subQuestions.length > 0;
      let finalNum = q.questionNumber;
      if (typeof finalNum === 'string' && finalNum.startsWith('Q')) {
        finalNum = parseInt(finalNum.replace('Q', ''), 10);
      }
      if (!finalNum || isNaN(finalNum)) finalNum = idx + 1;
      
      return {
        ...(q._id ? { _id: q._id } : {}), 
        sectionId: section.sectionId, 
        sectionName: section.sectionName,
        questionNumber: finalNum, 
        questionText: `Question Structure Q${finalNum}`, 
        marks: Number(q.marks),
        coMappings: enforceCOPOMapping && !hasSubQs ? (q.coMappings || []) : [],
        poMappings: enforceCOPOMapping && !hasSubQs ? (q.poMappings || []) : [],
        bloomLevel: hasSubQs ? '' : (enforceCOPOMapping ? (q.bloomLevel || 'understand') : ''), 
        hasSubQuestions: hasSubQs,
        subQuestions: (q.subQuestions || []).map((sq, sIdx) => ({
          subQuestionNumber: sq.subQuestionNumber || ['a', 'b', 'c'][sIdx] || 'z', 
          questionText: `Sub-part ${(sq.subQuestionNumber || '').toUpperCase()}`, 
          marks: Number(sq.marks),
          coMappings: enforceCOPOMapping ? (sq.coMappings || []) : [],
          poMappings: enforceCOPOMapping ? (sq.poMappings || []) : [],
          bloomLevel: enforceCOPOMapping ? (sq.bloomLevel || 'understand') : ''
        }))
      };
    })
  }));



  const handleFinalize = async () => {
    if (!isPreviewStage) {
      setFeedback({
        open: true,
        message: 'Review the structure blueprint carefully before locking it in.'
      });
      setIsPreviewStage(true);
      return;
    }
    if (!validateFinalTotal()) return;
    if (!acceptPreconditions) return;

    setSaving(true);
    try {
      const formattedSections = buildPayload();
      
      let response;
      if (!assignment) {
        const createEndpoint = isEndExam 
          ? '/api/faculty/end-exam-paper-templates/assign'
          : '/api/faculty/paper-template-assign';
        
        response = await api.post(createEndpoint, { 
          ...context, 
          templateId: template._id, 
          sections: formattedSections, 
          isFinalized: true, 
          facultyId: context.facultyId,
          cieTemplateId: context.cieTemplateId
        });
      } else {
        const updateEndpoint = isEndExam 
          ? `/api/faculty/end-exam-paper-templates/${assignment._id}/questions`
          : `/api/faculty/paper-template-assignment/${assignment._id}/questions`;
        
        response = await api.put(updateEndpoint, { 
          sections: formattedSections, 
          isFinalized: true, 
          facultyId: context.facultyId 
        });
      }

      if (response.data.success && onUpdateAssignment) {
        const a = response.data.assignment || response.data.data;
        onUpdateAssignment(a.componentId, a.itemId, a);
      }
      onSaveAndFinalize(response.data.assignment || response.data.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Finalization matrix build execution rejected.');
    } finally {
      setSaving(false);
    }
  };

  const handleFeedbackClose = () => {
    setFeedback({ open: false, message: '' });
  };

  if (!open) return null;

  return (
    <>
      <FeedbackModal open={feedback.open} message={feedback.message} onClose={handleFeedbackClose} />
      <div className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
        <div className="bg-slate-50 w-full rounded-xl max-w-6xl h-[92vh] sm:h-[85vh] flex flex-col shadow-xl border border-slate-200 overflow-hidden">
          
          {/* BANNER ROW */}
          <div className="bg-white px-4 sm:px-5 py-3.5 border-b border-slate-200 flex justify-between items-center shadow-sm shrink-0">
            <div>
              <h2 className="text-xs sm:text-sm font-black text-slate-800 uppercase tracking-wider">
                {isPreviewStage ? '📋 Structural Assessment Audit Pre-Check' : (template?.name || 'Evaluation Matrix Configurator')}
              </h2>
              <p className="text-[10px] font-bold text-indigo-600 uppercase mt-0.5">
                {isEndExam ? '📝 END EXAMINATION' : '📚 CONTINUOUS INTERNAL EVALUATION'} &bull; {context.subjectName || 'Unspecified Course Core'}
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 font-bold text-2xl px-2">&times;</button>
          </div>

          {/* ERROR WARNING HUB */}
          {error && (
            <div className="bg-rose-500 text-white px-4 py-2 text-[10px] sm:text-xs font-bold uppercase tracking-wider shrink-0 flex items-center gap-2">
              ⚠️ Warning Constraints Alert: {error}
            </div>
          )}

          {/* ACTIVE WORKSPACE ZONE */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-5 bg-slate-50 space-y-4">
            {isPreviewStage ? (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 text-xs font-semibold leading-relaxed">
                  🚨 Confirming commits locks paper structural blueprints definitively. This database transaction runtime cannot be reversed.
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 space-y-4 shadow-sm">
                  <h3 className="text-xs font-black uppercase text-slate-400 tracking-wider">Finalized Structural Blueprint Map</h3>
                  <div className="divide-y divide-slate-100">
                    {sections.map((section, sIdx) => (
                      <div key={section.sectionId || sIdx} className="py-3 first:pt-0 last:pb-0">
                        <div className="flex justify-between text-xs font-extrabold text-slate-800 mb-2 bg-slate-50 p-2 rounded">
                          <span>Section {section.sectionName}</span>
                          <span className="font-mono">{section.questions.reduce((sum, q) => sum + (q.marks || 0), 0)} / {section.sectionMaxMarks}m</span>
                        </div>
                        {section.questions.length === 0 ? (
                          <p className="text-xs text-slate-400 italic pl-2">No components mapped inside section parameters.</p>
                        ) : (
                          <div className="space-y-2 pl-1">
                            {section.questions.map((q, qIdx) => (
                              <div key={qIdx} className="text-xs text-slate-700 bg-white border border-slate-100 p-2.5 rounded shadow-sm">
                                <div className="flex justify-between font-bold">
                                  <span>Question {q.questionNumber || `Q${qIdx + 1}`}</span>
                                  <span className="font-mono text-indigo-600">{q.marks} Marks</span>
                                </div>
                                {q.subQuestions?.length > 0 ? (
                                  <div className="mt-2 pl-4 space-y-1 border-l-2 border-indigo-100 bg-slate-50/50 p-1.5 rounded">
                                    {q.subQuestions.map((subQ, subIndex) => (
                                      <div key={subIndex} className="flex flex-col gap-1 mb-2">
                                        <div className="flex justify-between text-[11px] text-slate-600">
                                          <span>
                                            Part <span className="font-bold uppercase text-slate-800">{subQ.subQuestionNumber}</span>
                                            {enforceCOPOMapping && (
                                              <span className="text-slate-400 font-medium ml-1">({subQ.bloomLevel})</span>
                                            )}
                                          </span>
                                          <span className="font-mono font-bold text-slate-500">{subQ.marks}m</span>
                                        </div>
                                        {(!subQ.subQuestions || subQ.subQuestions.length === 0) && enforceCOPOMapping && (
                                          <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500 bg-slate-50 p-1 rounded border border-slate-100">
                                            <span className="capitalize font-semibold text-slate-600">🧠 Level: {subQ.bloomLevel || 'understand'}</span>
                                            <span className="text-slate-300">|</span>
                                            <span className="font-medium">
                                              COs: {subQ.coMappings?.map(m => `CO${m.coNumber} (${m.weightage}%)`).join(', ') || 'None'}
                                            </span>
                                            <span className="text-slate-300">|</span>
                                            <span className="font-medium">
                                              POs: {subQ.poMappings?.map(m => `PO${m.poNumber} (${m.weightage}%)`).join(', ') || 'None'}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  enforceCOPOMapping && (
                                    <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px] text-slate-500 bg-slate-50 p-1.5 rounded border border-slate-100">
                                      <span className="capitalize font-semibold text-slate-600">🧠 Level: {q.bloomLevel || 'understand'}</span>
                                      <span className="text-slate-300">|</span>
                                      <span className="font-medium">
                                        COs: {q.coMappings?.map(m => `CO${m.coNumber} (${m.weightage}%)`).join(', ') || 'None'}
                                      </span>
                                      <span className="text-slate-300">|</span>
                                      <span className="font-medium">
                                        POs: {q.poMappings?.map(m => `PO${m.poNumber} (${m.weightage}%)`).join(', ') || 'None'}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white border rounded-xl p-4 flex items-center gap-3">
                  <input
                    id="final-lock-cb"
                    type="checkbox"
                    checked={acceptPreconditions}
                    onChange={(e) => setAcceptPreconditions(e.target.checked)}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded cursor-pointer"
                  />
                  <label htmlFor="final-lock-cb" className="text-xs font-bold text-slate-700 cursor-pointer select-none">
                    I verify that all manually designated sequence trackers, evaluation metrics mapping matrices, and totals are structurally accurate.
                  </label>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-white border rounded-xl p-3 flex justify-between items-center text-xs font-bold shadow-sm flex-wrap gap-2">
                  <span className="text-slate-500 uppercase">Gross Aggregate Matrix Blueprint Profile:</span>
                  <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs sm:text-sm font-black font-mono">
                    {totalMarks} / {targetTotalMarks || template?.maxMarks || 0} Marks Configured
                  </span>
                </div>

                {/* SECTION HEADERS TAB BAR */}
                <div className="flex gap-1 border-b border-slate-200 overflow-x-auto scrollbar-none">
                  {sections.map((section, idx) => {
                    const tabTotal = section.questions.reduce((sum, q) => sum + (q.marks || 0), 0);
                    return (
                      <button
                        key={section.sectionId}
                        type="button"
                        onClick={() => setActiveSection(idx)}
                        className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-t-lg transition-all whitespace-nowrap ${
                          activeSection === idx
                            ? 'bg-white border-t border-x border-slate-200 text-indigo-600 -mb-px shadow-sm'
                            : 'text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        Section {section.sectionName} ({tabTotal}m)
                      </button>
                    );
                  })}
                </div>

                {/* EDITOR TAB CONTENT DESK */}
                <div className="bg-white rounded-b-xl p-2 sm:p-4 border-x border-b border-slate-200 shadow-sm">
                  {sections.map((section, sectionIdx) => (
                    <div key={section.sectionId} className={activeSection === sectionIdx ? 'block' : 'hidden'}>
                      <SectionPanel
                        section={section}
                        onUpdateSection={(updated) => {
                          setSections(prev => {
                            const clone = [...prev];
                            clone[sectionIdx] = updated;
                            return clone;
                          });
                        }}
                        coPoCounts={coPoCounts}
                        enforceCOPOMapping={enforceCOPOMapping}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* CONTROLS FOOTER ACTION HUB */}
          <div className="bg-white px-4 sm:px-5 py-3.5 border-t border-slate-200 flex flex-wrap justify-end gap-2 shrink-0 shadow-lg">
            {isPreviewStage ? (
              <>
                <button
                  type="button"
                  onClick={() => setIsPreviewStage(false)}
                  className="w-full sm:w-auto px-4 py-2 border rounded-xl bg-white text-slate-700 text-xs font-bold hover:bg-slate-50 transition-colors"
                >
                  ← Return to Editor Desk
                </button>
                <button
                  type="button"
                  onClick={handleFinalize}
                  disabled={saving || !acceptPreconditions}
                  className="w-full sm:w-auto px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold disabled:opacity-40 transition-all shadow-md"
                >
                  Confirm & Lock Blueprint Structure
                </button>
              </>
            ) : (
              <>
                <button type="button" onClick={onClose} className="w-full sm:w-auto px-4 py-2 border rounded-xl bg-white text-slate-500 text-xs font-bold hover:bg-slate-50 transition-colors">Cancel</button>
                <button type="button" onClick={handleFinalize} disabled={saving} className="w-full sm:w-auto px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md">
                  Review Structure Blueprint →
                </button>
              </>
            )}
          </div>

        </div>
      </div>
    </>
  );
}